import React from 'react';

const formatDateTime = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const datePart = d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
  const timePart = d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
  return `${datePart}, ${timePart}`;
};

const DASH = '—';

const StageRow = ({ label, stage, note }) => {
  const status = stage?.status || 'pending';
  const decidedAt = formatDateTime(stage?.decidedAt);
  const hasNote = note && String(note).trim().length > 0;

  return (
    <div className={`gatepass-popup-stage-note-item ${status}`}>
      <div className="gatepass-popup-stage-note-header">
        <span className="gatepass-popup-stage-note-label">{label}</span>
        <span className={`gatepass-popup-stage-note-status ${status}`}>
          {status === 'approved' ? 'Approved' : status === 'rejected' ? 'Rejected' : 'Pending'}
        </span>
        <span className="gatepass-popup-stage-note-time">
          {decidedAt || DASH}
        </span>
      </div>
      <div className="gatepass-popup-stage-note-body">
        <span className="gatepass-popup-stage-note-key">Note:</span>
        <span className={`gatepass-popup-stage-note-value ${hasNote ? '' : 'empty'}`}>
          {hasNote ? note : DASH}
        </span>
      </div>
    </div>
  );
};

// Renders stage approvals + notes for an outstation gatepass.
// For BTech:      officeSecretary -> dugc -> hod -> hostelOffice
// For MBA/MSC:    officeSecretary -> dpgc -> hod -> hostelOffice
// For PhD:        instructor -> officeSecretary -> dpgc -> hod -> dean -> hostelOffice
// For midEndSemBreak: hostelOffice only (request goes directly to Hostel Office)
const GatepassStageNotes = ({ details }) => {
  if (!details) return null;

  const course = (details.course || '').toLowerCase();
  const isPhD = course === 'phd';
  // MBA and MSC are reviewed by DPGC instead of DUGC, and skip the Dean stage.
  // Must stay in sync with DPGC_COURSES in backend/utils/courseWorkflow.js.
  const isDpgcCourse = course === 'mba' || course === 'msc';
  const isSB = details.gatepassType === 'midEndSemBreak';
  const s = details.stageStatus || {};

  let stages;
  if (isSB) {
    stages = [
      { key: 'hostelOffice', label: 'Hostel Office', note: null, stage: s.hostelOffice },
    ];
  } else if (isDpgcCourse) {
    stages = [
      { key: 'officeSecretary', label: 'Office Secretary', note: details.officeSecretaryNote, stage: s.officeSecretary },
      { key: 'dpgc', label: 'DPGC', note: details.dpgcNote, stage: s.dpgc },
      { key: 'hod', label: 'HOD', note: details.hodNote, stage: s.hod },
      { key: 'hostelOffice', label: 'Hostel Office', note: null, stage: s.hostelOffice },
    ];
  } else if (isPhD) {
    stages = [
      { key: 'instructor', label: 'Instructor', note: details.instructorNote, stage: s.instructor },
      { key: 'officeSecretary', label: 'Office Secretary', note: details.officeSecretaryNote, stage: s.officeSecretary },
      { key: 'dpgc', label: 'DPGC', note: details.dpgcNote, stage: s.dpgc },
      { key: 'hod', label: 'HOD', note: details.hodNote, stage: s.hod },
      { key: 'dean', label: 'Dean', note: details.deanNote, stage: s.dean },
      { key: 'hostelOffice', label: 'Hostel Office', note: null, stage: s.hostelOffice },
    ];
  } else {
    stages = [
      { key: 'officeSecretary', label: 'Office Secretary', note: details.officeSecretaryNote, stage: s.officeSecretary },
      { key: 'dugc', label: 'DUGC', note: details.dugcNote, stage: s.dugc },
      { key: 'hod', label: 'HOD', note: details.hodNote, stage: s.hod },
      { key: 'hostelOffice', label: 'Hostel Office', note: null, stage: s.hostelOffice },
    ];
  }

  return (
    <div className="gatepass-popup-stage-notes">
      <h5>Stage Approvals &amp; Notes</h5>
      <div className="gatepass-popup-stage-notes-list">
        {stages.map((st) => (
          <StageRow key={st.key} label={st.label} stage={st.stage} note={st.note} />
        ))}
      </div>
    </div>
  );
};

// Renders extra fields: previousLeavesTaken, classesMissed, missedDays, rejectionReason, phdLeaveBalance
export const GatepassExtraInfo = ({ details }) => {
  if (!details) return null;

  const items = [];
  const pushItem = (label, value, fullWidth = false) => {
    const hasValue = value !== null && value !== undefined && String(value).trim() !== '';
    items.push({ label, value: hasValue ? value : DASH, fullWidth });
  };

  pushItem('Classes Missed', details.classesMissed);
  if (details.classesMissed === 'yes') {
    pushItem('Missed Days', details.missedDays);
  }
  pushItem('Previous Leaves Taken', details.previousLeavesTaken, true);

  if (details.finalStatus === 'rejected' && details.rejectionReason) {
    pushItem('Rejection Reason', details.rejectionReason, true);
  }

  if (items.length === 0) return null;

  return (
    <div className="gatepass-popup-details">
      {items.map((it, i) => (
        <div key={i} className={`gatepass-popup-detail-item${it.fullWidth ? ' full-width' : ''}`}>
          <span className="gatepass-popup-detail-label">{it.label}</span>
          <span className="gatepass-popup-detail-value">{it.value}</span>
        </div>
      ))}
    </div>
  );
};

export default GatepassStageNotes;
